import moment from 'moment';
import axios from 'axios';
import { createAnalyticsRequests } from '@/helpers/analyticsRequests';
import { localTimezone } from '@helpers/common.helper';
import API from '@api/analytics.api';
import corporateApi from '@/api/corporate.api';
import pagination from '../pagination';

/**
 * We use this constants to determine which action/mutation we should apply.
 * mostly used as prop for AnalyticsTable.vue
 */
const tablePaginationMutation = {
  usageTable: 'ADD_USAGE_TABLE_DATA',
  mostActiveProspectsTable: 'ADD_MOST_ACTIVE_PROSPECTS_TABLE_DATA',
  leaderboardTable: 'ADD_LEADERBOARD_TABLE_DATA',
  prospectEngagedTable: 'ADD_PROSPECT_ENGAGED_TABLE_DATA',
  insightsTable: 'ADD_INSIGHTS_TABLE_DATA'
};
const tablePaginationPageMutation = {
  mostActiveProspectsTable: 'SET_PAGE_MOST_ACTIVE_PROSPECTS',
  prospectEngagedTable: 'SET_PAGE_PROSPECTS_ENGAGED'
};
const tablePageState = {
  mostActiveProspectsTable: 'pageMostActiveProspectsTable',
  prospectEngagedTable: 'pageEngagedTable'
};
const tableShowMoreMutation = {
  mostActiveProspectsTable: 'SET_SHOW_MORE_MOST_ACTIVE_PROSPECTS',
  prospectEngagedTable: 'SET_SHOW_MORE_PROSPECTS_ENGAGED'
};
const tableDataMutation = {
  usageTable: 'SET_USAGE_TABLE_DATA',
  mostActiveProspectsTable: 'SET_MOST_ACTIVE_PROSPECTS_TABLE_DATA',
  mostActiveProspectsSmallTableColumns: 'SET_MOST_ACTIVE_PROSPECTS_SMALL_TABLE_DATA',
  leaderboardTable: 'SET_LEADERBOARD_TABLE_DATA',
  prospectEngagedTable: 'SET_PROSPECT_ENGAGED_TABLE_DATA',
  insightsTable: 'SET_INSIGHTS_TABLE_DATA'
};
const apiMethod = {
  propertyVideosCreated: 'getPropertyMostActiveVideo',
  propertyLvtCount: 'getPropertyLvtCount',
  propertyUniqueLinks: 'getPropertyLinksCounts',
  // Table Actions
  usageTable: 'getPropertyListCounts',
  mostActiveProspectsTable: 'getMostActiveProspects',
  mostActiveProspectsSmallTableColumns: 'getMostActiveProspects',
  leaderboardTable: 'getLeaderboardTableCounts',
  prospectEngagedTable: 'getMostActiveProspects',
  insightsTable: 'getInsightsCountsTable'
};

const normalizeModalDataByType = function (type, data) {
  const structuredData = {
    propertyVideosCreated: {
      modalType: type,
      videosCount: data.videosCount,
      viewsCount: data.videosViewsCount,
      duration: data.videosDuration?.total,
      sectionOneSecondaryData: data.videosDuration?.average,
      videoOptions: { ...data.mostActiveVideo, sources: [{ src: data.mostActiveVideo?.videoURL }] }
    },
    propertyLvtCount: {
      modalType: type,
      lvtCount: data.lvtCount,
      duration: data.completedLvtDuration?.total,
      sectionOneSecondaryData: data.completedLvtDuration?.average,
      sectionTwoMainData: data.lvtStatus?.scheduled,
      sectionTwoSecondaryData: data.lvtStatus?.completed,
      videoOptions: { ...data.video, sources: [{ src: data.video?.videoURL }] }
    }
  };

  if (type === 'propertyUniqueLinks') {
    const links = Object.entries(data.links).flatMap((value) => {
      if (value[1] < 1) return [];

      return {
        icon: value[0],
        channel: value[0],
        links: value[1]
      };
    });

    structuredData[type] = { links };
  }

  const propertyDetails = {
    propertyName: data?.propertyName || 'Property Name',
    propertyLogo: data?.propertyLogo
  };

  return { ...propertyDetails, ...structuredData[type] };
};

function initialState() {
  return {
    ...pagination.state,
    timezone: localTimezone(),
    loading: false,
    filtersChanged: false,
    dataViewComponent: '',
    mostActiveVideo: {},
    analyticsFilter: {},
    usageViewFilter: {},
    engagementViewFilter: {},
    insightsViewFilter: {},
    leaderboardViewFilter: {},
    liveEventInsightsType: '',
    liveEventInsightsAgent: null,
    // Charts
    chartSchema: [],
    countsByPeriod: [],
    usageCounts: {},
    viewsByChannels: [],
    viewsByVideoTypes: [],
    viewsByMediaTypes: [],
    engagementData: {},
    insightsMainData: {},
    lastYardiIntegrationDate: null,
    leadersByVideos: [],
    leadersByLinks: [],
    leadersByLvt: [],
    // Tables
    usageTableData: [],
    insightsTableData: [],
    mostActiveProspectsTableData: [],
    mostActiveProspectsSmallTableData: [],
    leaderboardTableData: [],
    prospectEngagedTableData: [],
    showMoreProspectsEngaged: false,
    showMoreMostActiveProspects: false,
    pageEngagedTable: 1,
    pageMostActiveProspectsTable: 1,
    activeTableColumn: '',
    activeTableSort: '',
    // Modals
    propertyDetailsModalType: '',
    prospectEngagedModalData: {},
    propertyDetailsModalData: {},
    propertyDetailsModalLoading: false,
    mostActiveProspectsCount: 0
  };
}

const getters = {
  ...pagination.getters,
  loading: (state) => state.loading,
  timezone: (state) => state.timezone,
  filtersChanged: (state) => state.filtersChanged,
  dataViewComponent: (state) => state.dataViewComponent,
  mostActiveVideo: (state) => state.mostActiveVideo,
  analyticsFilter: (state) => state.analyticsFilter,
  usageViewFilter: (state) => state.usageViewFilter,
  engagementViewFilter: (state) => state.engagementViewFilter,
  insightsViewFilter: (state) => state.insightsViewFilter,
  leaderboardViewFilter: (state) => state.leaderboardViewFilter,
  liveEventInsightsType: (state) => state.liveEventInsightsType,
  liveEventInsightsAgent: (state) => state.liveEventInsightsAgent,
  // Charts
  chartSchema: (state) => state.chartSchema,
  countsByPeriod: (state) => state.countsByPeriod,
  usageCounts: (state) => state.usageCounts,
  viewsByChannels: (state) => state.viewsByChannels,
  viewsByVideoTypes: (state) => state.viewsByVideoTypes,
  viewsByMediaTypes: (state) => state.viewsByMediaTypes,
  engagementData: (state) => state.engagementData,
  insightsMainData: (state) => state.insightsMainData,
  lastYardiIntegrationDate: (state) => state.lastYardiIntegrationDate,
  leadersByVideos: (state) => state.leadersByVideos,
  leadersByLinks: (state) => state.leadersByLinks,
  leadersByLvt: (state) => state.leadersByLvt,
  // Tables
  usageTableData: (state) => state.usageTableData,
  insightsTableData: (state) => state.insightsTableData,
  mostActiveProspectsTableData: (state) => state.mostActiveProspectsTableData,
  mostActiveProspectsSmallTableData: (state) => state.mostActiveProspectsSmallTableData,
  leaderboardTableData: (state) => state.leaderboardTableData,
  prospectEngagedTableData: (state) => state.prospectEngagedTableData,
  activeTableColumn: (state) => state.activeTableColumn,
  activeTableSort: (state) => state.activeTableSort,
  showMoreProspectsEngaged: (state) => state.showMoreProspectsEngaged,
  showMoreMostActiveProspects: (state) => state.showMoreMostActiveProspects,
  pageEngagedTable: (state) => state.pageEngagedTable,
  pageMostActiveProspectsTable: (state) => state.pageMostActiveProspectsTable,
  // Modals
  prospectEngagedModalData: (state) => state.prospectEngagedModalData,
  propertyDetailsModalType: (state) => state.propertyDetailsModalType,
  propertyDetailsModalData: (state) => state.propertyDetailsModalData,
  propertyDetailsModalLoading: (state) => state.propertyDetailsModalLoading,
  mostActiveProspectsCount: (state) => state.mostActiveProspectsCount
};

const tableMutations = {
  SET_ACTIVE_TABLE_COLUMN(state, payload) {
    state.activeTableColumn = payload;
  },
  SET_ACTIVE_TABLE_SORT(state, payload) {
    state.activeTableSort = payload;
  },
  // Usage View
  SET_USAGE_TABLE_DATA(state, payload) {
    state.usageTableData = payload;
  },
  ADD_USAGE_TABLE_DATA(state, payload) {
    state.usageTableData.push(...payload);
  },
  // Insights View
  SET_INSIGHTS_TABLE_DATA(state, payload) {
    state.insightsTableData = payload;
  },
  ADD_INSIGHTS_TABLE_DATA(state, payload) {
    state.insightsTableData.push(...payload);
  },
  // Leaderboard View
  SET_LEADERBOARD_TABLE_DATA(state, payload) {
    state.leaderboardTableData = payload;
  },
  ADD_LEADERBOARD_TABLE_DATA(state, payload) {
    state.leaderboardTableData.push(...payload);
  },
  SET_MOST_ACTIVE_PROSPECTS_TABLE_DATA(state, payload) {
    state.mostActiveProspectsTableData = payload;
  },
  ADD_MOST_ACTIVE_PROSPECTS_TABLE_DATA(state, payload) {
    state.mostActiveProspectsTableData.push(...payload);
  },
  SET_MOST_ACTIVE_PROSPECTS_SMALL_TABLE_DATA(state, payload) {
    state.mostActiveProspectsSmallTableData = payload;
  },
  // Prospect Engaged Modal table
  SET_PROSPECT_ENGAGED_TABLE_DATA(state, payload) {
    state.prospectEngagedTableData = payload;
  },
  ADD_PROSPECT_ENGAGED_TABLE_DATA(state, payload) {
    state.prospectEngagedTableData.push(...payload);
  },
  SET_PAGE_MOST_ACTIVE_PROSPECTS(state, payload) {
    state.pageMostActiveProspectsTable = payload;
  },
  SET_PAGE_PROSPECTS_ENGAGED(state, payload) {
    state.pageEngagedTable = payload;
  },
  SET_SHOW_MORE_PROSPECTS_ENGAGED(state, payload) {
    state.showMoreProspectsEngaged = payload;
  },
  SET_SHOW_MORE_MOST_ACTIVE_PROSPECTS(state, payload) {
    state.showMoreMostActiveProspects = payload;
  }
};

const chartsMutations = {
  // Usage View
  SET_CHART_SCHEMA(state, payload) {
    state.chartSchema = payload;
  },
  SET_COUNTS_BY_PERIOD(state, payload) {
    if (payload && payload.length) {
      const lastDayWithData = payload[payload.length - 1][0];
      const plusOneDay = moment(lastDayWithData, 'MM/DD/YYYY').add(1, 'day').format('MM/DD/YYYY');
      payload.push([
        plusOneDay, 0, 0, 0, 0, 0, 0, 0, 0
      ]);
    }
    state.countsByPeriod = payload;
  },
  SET_USAGE_COUNTS(state, payload) {
    state.usageCounts = payload;
  },
  // Engagement View
  SET_ENGAGEMENT_DATA(state, payload) {
    state.engagementData = payload;
  },
  SET_VIEWS_BY_CHANNELS(state, payload) {
    state.viewsByChannels = payload;
  },
  SET_VIEWS_BY_VIDEO_TYPES(state, payload) {
    state.viewsByVideoTypes = payload;
  },
  SET_VIEWS_BY_MEDIA_TYPES(state, payload) {
    state.viewsByMediaTypes = payload;
  },
  // Insights View
  SET_INSIGHTS_MAIN_DATA(state, payload) {
    state.insightsMainData = payload;
  },
  // Leaderboard View
  SET_LEADERS_BY_VIDEOS(state, payload) {
    state.leadersByVideos = payload;
  },
  SET_LEADERS_BY_LINKS(state, payload) {
    state.leadersByLinks = payload;
  },
  SET_LEADERS_BY_LT_LS(state, payload) {
    state.leadersByLvt = payload;
  },
  SET_MOST_ACTIVE_VIDEO(state, payload) {
    state.mostActiveVideo = payload;
  },
  SET_MOST_ACTIVE_PROSPECTS_COUNT(state, loading) {
    state.mostActiveProspectsCount = loading;
  }
};

const mutations = {
  ...pagination.mutations,
  ...tableMutations,
  ...chartsMutations,
  // Discard results, cached filters, and pagination while keeping the active selection.
  CLEAR_RESULTS(state) {
    const initial = initialState();
    const preserved = [
      'timezone', 'analyticsFilter', 'dataViewComponent', 'filtersChanged',
      'liveEventInsightsType', 'liveEventInsightsAgent', 'lastYardiIntegrationDate',
      'activeTableColumn', 'activeTableSort'
    ];
    Object.keys(initial).filter((key) => !preserved.includes(key)).forEach((key) => {
      state[key] = initial[key];
    });
  },
  SET_LOADING(state, payload) {
    state.loading = payload;
  },
  SET_FILTERS_CHANGED(state, payload) {
    state.filtersChanged = payload;
  },
  SET_USAGE_VIEW_FILTERS(state, payload) {
    state.usageViewFilter = payload;
  },
  SET_ENGAGEMENT_VIEW_FILTERS(state, payload) {
    state.engagementViewFilter = payload;
  },
  SET_INSIGHTS_VIEW_FILTERS(state, payload) {
    state.insightsViewFilter = payload;
  },
  SET_LEADERBOARD_VIEW_FILTERS(state, payload) {
    state.leaderboardViewFilter = payload;
  },
  SET_ANALYTICS_FILTER(state, payload) {
    state.analyticsFilter = payload;
  },
  SET_ACTIVE_DATA_VIEW_COMPONENT(state, payload) {
    state.dataViewComponent = payload;
  },
  SET_PROPERTY_DETAILS_MODAL_TYPE(state, type) {
    state.propertyDetailsModalType = type;
  },
  SET_PROPERTY_DETAILS_MODAL_DATA(state, payload) {
    state.propertyDetailsModalData = payload;
  },
  SET_PROSPECT_ENGAGED_MODAL_DATA(state, payload) {
    state.prospectEngagedModalData = payload;
  },
  SET_PROPERTY_DETAILS_MODAL_LOADING(state, loading) {
    state.propertyDetailsModalLoading = loading;
  },
  SET_LAST_YARDI_INTEGRATION_DATE(state, payload) {
    state.lastYardiIntegrationDate = payload;
  },
  SET_LIVE_EVENT_INSIGHTS_TYPE(state, payload) {
    state.liveEventInsightsType = payload;
  },
  SET_LIVE_EVENT_INSIGHTS_AGENT(state, payload) {
    state.liveEventInsightsAgent = payload;
  },
  RESET(state) {
    const initial = initialState();

    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  }
};

const requests = createAnalyticsRequests();

// Each action owns its API calls and checks isCurrent() before committing responses.
// Reusing a key cancels the previous request group; different keys can load concurrently.
async function runRequest(commit, key, load) {
  const request = requests.start(key);
  commit('SET_LOADING', true);
  try {
    const result = await load(request);
    return request.isCurrent() && result !== false;
  } catch (error) {
    if (request.isCurrent() && !axios.isCancel(error)) console.error(error);
    return false;
  } finally {
    if (request.isCurrent()) {
      request.finish();
      // Another request group may still be loading after this one finishes.
      commit('SET_LOADING', requests.pending());
    }
  }
}

const actions = {
  invalidateRequests({ commit }) {
    requests.invalidate();
    commit('CLEAR_RESULTS');
  },
  getTableData({ state, commit }, { params, tableNameKey }) {
    return runRequest(commit, `table:${tableNameKey}`, async (request) => {
      const pageMutation = tablePaginationPageMutation[tableNameKey] || 'SET_PAGE';
      const showMoreMutation = tableShowMoreMutation[tableNameKey] || 'SET_SHOW_MORE';
      commit(pageMutation, 1);
      commit(showMoreMutation, false);
      commit(tableDataMutation[tableNameKey], []);
      commit('SET_FETCHING_MORE', false);

      const response = await API[apiMethod[tableNameKey]]({
        ...params, timezone: state.timezone
      }, { signal: request.signal });
      if (!request.isCurrent() || response.status !== 200) return false;

      if (tableNameKey === 'mostActiveProspectsTable') {
        commit('SET_MOST_ACTIVE_PROSPECTS_COUNT', response.data.pagination.count);
      }
      commit(tableDataMutation[tableNameKey], response.data.records);
      commit(showMoreMutation, response.data.pagination.pages > 1);
      return true;
    });
  },
  // View filters describe successfully loaded data, never an in-flight selection.
  // Capture them before awaiting and only cache them after every response succeeds.
  getAllUsageCounts({ state, commit }, params) {
    const filters = { ...state.analyticsFilter };
    commit('SET_USAGE_VIEW_FILTERS', {});
    return runRequest(commit, 'usage', async (request) => {
      const options = { signal: request.signal };
      const query = { ...params, timezone: state.timezone };
      const [period, counts] = await Promise.all([
        API.getUsageCountsByPeriod(query, options),
        API.getUsageCounts(query, options)
      ]);
      if (!request.isCurrent() || period.status !== 200 || counts.status !== 200) return false;

      commit('SET_COUNTS_BY_PERIOD', period.data.records);
      commit('SET_CHART_SCHEMA', period.data.schema);
      commit('SET_USAGE_COUNTS', counts.data.records);
      commit('SET_USAGE_VIEW_FILTERS', filters);
      return true;
    });
  },
  resetUsageCounts({ commit }) {
    commit('SET_COUNTS_BY_PERIOD', []);
    commit('SET_CHART_SCHEMA', []);
    commit('SET_USAGE_COUNTS', []);
  },
  getInsightsMainData({ state, commit }, params) {
    const filters = { ...state.analyticsFilter };
    commit('SET_INSIGHTS_VIEW_FILTERS', {});
    return runRequest(commit, 'insights', async (request) => {
      const response = await API.getInsightsMainData({
        ...params, timezone: state.timezone
      }, { signal: request.signal });
      if (!request.isCurrent() || response.status !== 200) return false;

      commit('SET_INSIGHTS_MAIN_DATA', response.data.record);
      commit('SET_INSIGHTS_VIEW_FILTERS', filters);
      return true;
    });
  },
  getEngagementCharts({ state, commit }, params) {
    const filters = { ...state.analyticsFilter };
    commit('SET_ENGAGEMENT_VIEW_FILTERS', {});
    return runRequest(commit, 'engagement', async (request) => {
      const { hasVrContent, ...rest } = params;
      const query = { ...rest, timezone: state.timezone };
      const options = { signal: request.signal };
      const responses = await Promise.all([
        API.getEngagementViewsByChannels(query, options),
        API.getEngagementViewsByVideoTypes(query, options),
        API.getEngagementVideoData(query, options),
        API.getEngagementLiveData(query, options),
        ...(hasVrContent ? [API.getEngagementViewsByMediaType(query, options)] : [])
      ]);
      // Commit the whole group together to avoid mixing results from different selections.
      if (!request.isCurrent() || responses.some((response) => response.status !== 200)) return false;

      const [channels, types, videos, live, media] = responses;
      const chartValues = (records) => Object.entries(records).map(([label, value]) => ({ label, value }));
      commit('SET_VIEWS_BY_CHANNELS', chartValues(channels.data.records));
      commit('SET_VIEWS_BY_VIDEO_TYPES', chartValues(types.data.records));
      commit('SET_ENGAGEMENT_DATA', {
        ...videos.data.records, ...live.data.records, loadingFinished: true
      });
      commit('SET_VIEWS_BY_MEDIA_TYPES', media ? media.data.records.map((item) => ({
        label: item.contentType, value: item.totalViews
      })) : []);
      commit('SET_ENGAGEMENT_VIEW_FILTERS', filters);
      return true;
    });
  },
  getLeaderboardData({ state, commit }, { params, tableNameKey, getPropertiesLeaders }) {
    const filters = { ...state.analyticsFilter };
    commit('SET_LEADERBOARD_VIEW_FILTERS', {});
    return runRequest(commit, 'leaderboard', async (request) => {
      const query = {
        startDate: params.startDate,
        endDate: params.endDate,
        propertiesIds: params.propertiesIds,
        timezone: state.timezone
      };
      const options = { signal: request.signal };
      const responses = await Promise.all([
        API.getMostActiveVideo(query, options),
        API.getMostActiveProspects({ ...params, timezone: state.timezone }, options),
        ...(getPropertiesLeaders ? [
          API.getPropertiesLeadersByVideo(query, options),
          API.getPropertiesLeadersByLvt(query, options),
          API.getPropertiesLeadersByLinks(query, options)
        ] : [])
      ]);
      if (!request.isCurrent() || responses.some((response) => response.status !== 200)) return false;

      const [video, prospects, videoLeaders, lvtLeaders, linkLeaders] = responses;
      commit('SET_MOST_ACTIVE_VIDEO', video.data.record || { notFound: true });
      commit(tableDataMutation[tableNameKey], prospects.data.records);
      if (getPropertiesLeaders) {
        commit('SET_LEADERS_BY_VIDEOS', videoLeaders.data.records);
        commit('SET_LEADERS_BY_LT_LS', lvtLeaders.data.records);
        commit('SET_LEADERS_BY_LINKS', linkLeaders.data.records);
      }
      commit('SET_LEADERBOARD_VIEW_FILTERS', filters);
      return true;
    });
  },
  // Share the table reload key so a refresh cancels any pending pagination response.
  fetchMore({ state, commit }, { params, tableNameKey }) {
    return runRequest(commit, `table:${tableNameKey}`, async (request) => {
      const pageMutation = tablePaginationPageMutation[tableNameKey] || 'SET_PAGE';
      const showMoreMutation = tableShowMoreMutation[tableNameKey] || 'SET_SHOW_MORE';
      const nextPage = state[tablePageState[tableNameKey] || 'page'] + 1;
      commit('SET_FETCHING_MORE', true);
      try {
        const response = await API[apiMethod[tableNameKey]]({
          page: nextPage, q: state.filter, ...params, timezone: state.timezone
        }, { signal: request.signal });
        if (!request.isCurrent() || response.status !== 200) return false;

        commit(pageMutation, nextPage);
        commit(showMoreMutation, nextPage < response.data.pagination.pages);
        commit(tablePaginationMutation[tableNameKey], response.data.records);
        return true;
      } finally {
        if (request.isCurrent()) commit('SET_FETCHING_MORE', false);
      }
    });
  },
  getPropertyDetailsModal({ state, commit }, { type, params }) {
    return runRequest(commit, 'propertyDetails', async (request) => {
      commit('SET_PROPERTY_DETAILS_MODAL_TYPE', type);
      commit('SET_PROPERTY_DETAILS_MODAL_LOADING', true);
      try {
        const response = await API[apiMethod[type]]({
          ...params, timezone: state.timezone
        }, { signal: request.signal });
        if (!request.isCurrent() || response.status !== 200) return false;

        const { records, record } = response.data;
        commit('SET_PROPERTY_DETAILS_MODAL_DATA', normalizeModalDataByType(type, records || record));
        return true;
      } finally {
        if (request.isCurrent()) commit('SET_PROPERTY_DETAILS_MODAL_LOADING', false);
      }
    });
  },
  setPropertyDetailsModalData({ commit }, payload) {
    commit('SET_PROPERTY_DETAILS_MODAL_DATA', payload);
  },
  getInsightsLastYardiIntegrationDate({ commit }, propertyIds) {
    return runRequest(commit, 'integrationDate', async (request) => {
      const response = await corporateApi.getInsightsLastYardiIntegrationDate({
        propertyIds
      }, { signal: request.signal });
      if (!request.isCurrent() || response.status !== 200) return false;

      const { record } = response.data;
      const minimum = '2022-01-01';
      const date = record ? moment(record).subtract(90, 'days').format('YYYY-MM-DD') : minimum;
      commit('SET_LAST_YARDI_INTEGRATION_DATE', date > minimum ? date : minimum);
      return true;
    });
  },
  setProspectEngagedModalData({ commit }, payload) {
    commit('SET_PROSPECT_ENGAGED_MODAL_DATA', payload);
  },
  resetEngagementData({ commit }) {
    commit('SET_VIEWS_BY_CHANNELS', []);
    commit('SET_VIEWS_BY_MEDIA_TYPES', []);
    commit('SET_VIEWS_BY_VIDEO_TYPES', []);
    commit('SET_ENGAGEMENT_DATA', {});
  },
  resetAnalyticsData({ commit }) {
    requests.invalidate();
    commit('RESET');
  },
  setActiveDataViewComponent({ commit }, payload) {
    commit('SET_ACTIVE_DATA_VIEW_COMPONENT', payload);
  },
  setActiveTableColumn({ commit }, payload) {
    commit('SET_ACTIVE_TABLE_COLUMN', payload);
  },
  setActiveTableSort({ commit }, payload) {
    commit('SET_ACTIVE_TABLE_SORT', payload);
  },
  setFiltersChanged({ commit }, payload) {
    commit('SET_FILTERS_CHANGED', payload);
  },
  // Invalidate synchronously, before filter watchers start requests for the new selection.
  setAnalyticsFilter({ commit }, payload) {
    requests.invalidate();
    commit('CLEAR_RESULTS');
    commit('SET_ANALYTICS_FILTER', payload);
  },
  setLiveEventInsightsType({ commit }, payload) {
    requests.invalidate();
    commit('CLEAR_RESULTS');
    commit('SET_LIVE_EVENT_INSIGHTS_TYPE', payload);
  },
  setLiveEventInsightsAgent({ commit }, payload) {
    requests.invalidate();
    commit('CLEAR_RESULTS');
    commit('SET_LIVE_EVENT_INSIGHTS_AGENT', payload);
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions
};
